import React from 'react';
import { createPortal } from 'react-dom';
import * as CorgeeHelper from '../utils/CorgeeHelper';
import ConversationApi from './api/ConversationApi';
import {
  getSelectedConversationCount,
  getSelectedConversationIds,
  getSelectedConversationTitles,
} from './selectionUtils';

import MenuApi from './api/MenuApi';
import RenderInBody from '../Globals/RenderInBody';
import RenderBody from '@/components/utils/RenderInBody';

import EditDetailsHistory from './EditHistory/EditDetailsHistory';
import SubmitTime from '../SubmitTime/SubmitTime';
//import '../SubmitTime/styles.css';
import $ from 'jquery';
import type { Conversation, ConversationDetail } from '../../types/conversation';
import type { ApiConfig } from './types';
import ConvertToModal, { type ConversionResult } from './modals/ConvertToModal';
import DeleteConversationModel from './modals/DeleteConversationModel';
import { conversationService } from '@/services/conversationService';

import '../Globals/ComponentHelpers.css';
import './ActionMenuConversation.css';
import MoveCopyToModal from './modals/MoveCopyToModal';
import type { Dashboard } from '@/types/dashboard';
import type { SideMenuItem } from '@/services/menuService';
import CreatePhase from './modals/CreatePhase/CreatePhaseModal';
import EditMultipleCards from './modals/EditMultipleCards/EditMultipleCardsModal';
import ChangeProjectModal from './modals/ChangeProject/ChangeProjectModal';
import EditSecurityGroupsModal from './modals/EditSecurityGroupPermissions/EditSecurityGroupPermissionsModal';
import ToggleUserStatusModal from './modals/ToggleUserStatusModal';
import { authStorage } from '@/lib';
import { apiService } from '@/services/api';
import { conversationBus } from '@/lib/conversationBus';
import {
  validateYesterdayMetrics,
  computeCardsDisabled,
} from '@/hooks/useYesterdayMetricsValidation';
import { useNavigate } from 'react-router-dom';

type NavigateFn = ReturnType<typeof useNavigate>;

type MenuConfigItem = any;

// ─── Action Menu Architecture ──────────────────────────────────────────────────
// Each action declares when it is available and how it behaves in each context.
// Add new actions here — no changes needed elsewhere in the render logic.

/** Selection context passed to every action handler and visibility function */
export interface ActionMenuContext {
  /** The conversation that owns the clicked 3-dot button */
  conversation: ConversationDetail;
  /** All currently selected conversations (single-select: just [conversation]) */
  selectedConversations: ConversationDetail[];
  isMultiple: boolean;
  api: ApiConfig;
  SocketConnectionId?: string;
  corgeeFunctions?: ActionMenuConversationProps['corgeeFunctions'];
  groupIsDefault: boolean | null;
  /** Callbacks that open the in-component modals */
  openModal: {
    delete: () => void;
    archive: () => void;
    moveTo: () => void;
    copyTo: () => void;
    convertTo: () => void;
    createPhase: (e: React.MouseEvent) => void;
    editMultipleDetails: (e: React.MouseEvent) => void;
    changeProject: (e: React.MouseEvent) => void;
    submitTime: () => void;
    editHistory: () => void;
  };
  closeMenu: () => void;
  onMarkAsRedFlag: (e: React.MouseEvent) => void;
  onMarkAsImportant: (e: React.MouseEvent) => void;
  followState: number; // isUserFollowingConversation
  /** Updates local card state after a successful bulk operation */
  onUpdateConversation?: (conversationId: number, updates: Partial<ConversationDetail>) => void;
}

/**
 * When an action is available:
 *  'single' – only for one-card menus
 *  'multi'  – only when 2+ cards are selected
 *  'both'   – available in both contexts (behaviour may differ via handler)
 */
type SelectionMode = 'single' | 'multi' | 'both';

interface ActionMenuItemDef {
  key: string;
  mode: SelectionMode;
  icon: string;
  label: string | ((ctx: ActionMenuContext) => string);
  /** Return false to hide this item in the current context */
  visible?: (ctx: ActionMenuContext) => boolean;
  onClick: (ctx: ActionMenuContext, e: React.MouseEvent) => void;
}

/** Master list — order here is the render order */
const ACTION_MENU_ITEMS: ActionMenuItemDef[] = [
  // ── Available for BOTH single and multi ──────────────────────────────────
  {
    key: 'mark-redflag',
    mode: 'both',
    icon: 'corgee-icon-red-flag material-icons',
    label: ctx =>
    {
      if (ctx.isMultiple)
      {
        // Toggle based on first card's current state
        return ctx.selectedConversations[0]?.IsRedFlag ? 'Unmark As Redflag' : 'Mark As Redflag';
      }
      return ctx.conversation.IsRedFlag ? 'Unmark as Redflag' : 'Mark as Redflag';
    },
    onClick: (ctx, e) =>
    {
      if (ctx.isMultiple)
      {
        // Toggle: derive target state from the first selected card
        const targetState = !ctx.selectedConversations[0]?.IsRedFlag;
        const requestParams = {
          conversations: ctx.selectedConversations.map(c => ({
            ConfigValueId: c.ConfigValueId,
            ConversationId: c.ConversationId,
            CoreTaskId: c.CoreTaskId,
            CreatedByID: c.CreatedByID,
            Finished: c.Finished,
            Icon: c.Icon,
            IsRedFlag: !targetState,
            ProjectId: c.ProjectId,
            SideMenuText: c.SideMenuText,
            Title: c.Title,
            UpdatedAt: c.UpdatedAt,
            callService: true,
          })),
          SocketConnectionId: ctx.SocketConnectionId || '',
        };
        ConversationApi.updateRedFlags(ctx.api, requestParams)
          .then((response: any) =>
          {
            console.log('✅ updateRedFlags response:', response);
            // Update each card visually
            ctx.selectedConversations.forEach(c =>
            {
              ctx.onUpdateConversation?.(c.ConversationId, { IsRedFlag: targetState });
            });
            const ids = ctx.selectedConversations.map(c => c.ConversationId).join(',');
            ConversationApi.SetConversationsAsViewed(ctx.api, { ConversationIds: ids });
          })
          .catch((err: any) =>
          {
            console.error('❌ updateRedFlags error:', err);
          });
      }
      else
      {
        ctx.onMarkAsRedFlag(e);
      }
      ctx.closeMenu();
    },
  },
  {
    key: 'mark-important',
    mode: 'both',
    icon: 'material-icons',
    label: ctx =>
    {
      if (ctx.isMultiple)
      {
        return ctx.selectedConversations[0]?.IsImportant
          ? 'Unmark As Important'
          : 'Mark As Important';
      }
      return ctx.conversation.IsImportant ? 'Unmark as Important' : 'Mark as Important';
    },
    onClick: (ctx, e) =>
    {
      if (ctx.isMultiple)
      {
        const targetState = !ctx.selectedConversations[0]?.IsImportant;
        const requestParams = {
          conversations: ctx.selectedConversations.map(c => ({
            ConfigValueId: c.ConfigValueId,
            ConversationId: c.ConversationId,
            CoreTaskId: c.CoreTaskId,
            CreatedByID: c.CreatedByID,
            Finished: c.Finished,
            Icon: c.Icon,
            IsImportant: !targetState,
            ProjectId: c.ProjectId,
            SideMenuText: c.SideMenuText,
            Title: c.Title,
            UpdatedAt: c.UpdatedAt,
            callService: true,
          })),
          SocketConnectionId: ctx.SocketConnectionId || '',
        };
        ConversationApi.updateImportant(ctx.api, requestParams)
          .then((response: any) =>
          {
            console.log('✅ updateImportant response:', response);
            ctx.selectedConversations.forEach(c =>
            {
              ctx.onUpdateConversation?.(c.ConversationId, { IsImportant: targetState });
            });
            const ids = ctx.selectedConversations.map(c => c.ConversationId).join(',');
            ConversationApi.SetConversationsAsViewed(ctx.api, { ConversationIds: ids });
          })
          .catch((err: any) =>
          {
            console.error('❌ updateImportant error:', err);
          });
      }
      else
      {
        ctx.onMarkAsImportant(e);
      }
      ctx.closeMenu();
    },
  },
  {
    key: 'follow-unfollow',
    mode: 'both',
    icon: 'icons-menu-local-offer material-icons',
    label: ctx =>
      ctx.isMultiple
        ? 'Unfollow Conversations'
        : ctx.followState === 1
          ? 'Unfollow conversation'
          : 'Follow conversation',
    onClick: ctx =>
    {
      const convs = ctx.isMultiple ? ctx.selectedConversations : [ctx.conversation];
      const requestParams = {
        conversationsFollowers: convs.map(c => ({
          ConversationId: c.ConversationId,
          Delete: ctx.isMultiple ? 1 : ctx.followState === 1 ? 1 : 0,
        })),
      };
      ConversationApi.saveFollower(ctx.api, requestParams).catch(console.error);
      ctx.closeMenu();
    },
  },
  {
    key: 'edit-multiple-details',
    mode: 'multi',
    icon: 'material-icons corgee-icon-details',
    label: 'Edit Multiple Details',
    visible: ctx => ctx.conversation.ConfigValueId !== 15,
    onClick: (ctx, e) =>
    {
      ctx.openModal.editMultipleDetails(e);
    },
  },
  {
    key: 'change-project',
    mode: 'multi',
    icon: 'material-icons',
    label: 'Change Project',
    visible: ctx => ctx.conversation.ConfigValueId !== 15,
    onClick: (ctx, e) =>
    {
      ctx.openModal.changeProject(e);
    },
  },
  {
    key: 'convert-to',
    mode: 'both',
    icon: 'material-icons',
    label: 'Convert To',
    visible: ctx => (!ctx.isMultiple ? ctx.conversation.ConfigValueId !== 9 : true),
    onClick: ctx =>
    {
      ctx.openModal.convertTo();
      ctx.closeMenu();
    },
  },
  {
    key: 'remove-from-column',
    mode: 'both',
    icon: 'material-icons',
    label: 'Remove From Column',
    visible: ctx => !ctx.groupIsDefault,
    onClick: (ctx, e) =>
    {
      if (ctx.isMultiple && ctx.selectedConversations.length > 0)
      {
        ctx.corgeeFunctions?.onBulkRemoveFromColumn?.(
          ctx.selectedConversations as ConversationDetail[],
        );
      }
      else
      {
        ctx.corgeeFunctions?.onRemoveFromColumn?.(e, ctx.conversation as Conversation);
      }
      ctx.closeMenu();
    },
  },
  {
    key: 'delete',
    mode: 'both',
    icon: 'icon-trash-icon',
    label: 'Delete',
    onClick: ctx =>
    {
      ctx.openModal.delete();
      ctx.closeMenu();
    },
  },
  {
    key: 'archive',
    mode: 'both',
    icon: 'icon2-filedown material-icons',
    label: ctx =>
      ctx.isMultiple
        ? ctx.selectedConversations.every(c => c.IsArchived)
          ? 'Unarchive'
          : 'Archive'
        : ctx.conversation.IsArchived
          ? 'Unarchive'
          : 'Archive',
    visible: ctx => ctx.conversation.ConfigValueId !== 15,
    onClick: ctx =>
    {
      ctx.openModal.archive();
      ctx.closeMenu();
    },
  },
  {
    key: 'move-to',
    mode: 'both',
    icon: 'fa fa-share-square-o',
    label: 'Move To',
    // Hidden in host apps like Chat (no target dashboardId available); Copy To is used instead
    visible: ctx => !ctx.corgeeFunctions?.getBaseUrl,
    onClick: ctx =>
    {
      ctx.openModal.moveTo();
      ctx.closeMenu();
    },
  },
  {
    key: 'copy-to',
    mode: 'both',
    icon: 'fa fa-clone',
    label: 'Copy To',
    onClick: ctx =>
    {
      ctx.openModal.copyTo();
      ctx.closeMenu();
    },
  },

  // ── SINGLE only ──────────────────────────────────────────────────────────
  {
    key: 'copy-link',
    mode: 'single',
    icon: 'material-icons',
    label: 'Copy link to Clipboard',
    onClick: () =>
    {
      /* handled by existing onCopyToClipboard */
    },
  },
  {
    key: 'edit',
    mode: 'single',
    icon: 'icon-pencil-icon',
    label: 'Edit',
    onClick: () =>
    {
      /* handled by existing onEdit */
    },
  },
  {
    key: 'submit-time',
    mode: 'single',
    icon: 'material-icons',
    label: 'Submit Time',
    onClick: ctx =>
    {
      ctx.openModal.submitTime();
      ctx.closeMenu();
    },
  },

  // ── MULTI only ───────────────────────────────────────────────────────────
  {
    key: 'create-phase',
    mode: 'multi',
    icon: 'material-icons',
    label: 'Create Phase',
    visible: ctx => ctx.conversation.ConfigValueId !== 15,
    onClick: (ctx, e) =>
    {
      ctx.openModal.createPhase(e);
    },
  },
];

/** Map icon key → material-icons content string (for items that use text-based icons) */
const ICON_CONTENT: Record<string, string> = {
  'mark-important': 'priority_high',
  'convert-to': 'repeat',
  'remove-from-column': 'remove_circle_outline',
  'copy-link': 'link',
  'submit-time': 'schedule',
  'create-phase': 'library_books',
  'change-project': 'swap_horiz',
};
// ──────────────────────────────────────────────────────────────────────────────

interface ModalState {
  open: boolean;
  title: string;
  template: null | React.ReactNode;
  config?: any;
}

interface ActionMenuConversationProps {
  conversation: ConversationDetail;
  api: ApiConfig;
  config: {
    weburl: string;
  };
  corgeeFunctions?: {
    moveTo?: (e: React.MouseEvent, conversation: Conversation) => void;
    copyTo?: (e: React.MouseEvent, conversation: Conversation) => void;
    editConversation?: (e: React.MouseEvent, conversation: Conversation) => void;
    deleteConversation?: (e: React.MouseEvent, conversation: Conversation) => void;
    archiveConversation?: (
      e: React.MouseEvent,
      conversation: Conversation,
      callback?: () => void
    ) => void;
    goToProject?: (e: React.MouseEvent, conversation: Conversation) => void;
    onCloseInfoWindow?: (conversation: Conversation) => void;
    openInfoWindow?: (conversation: Conversation) => void;
    onDuplicateProject?: (e: React.MouseEvent, conversation: Conversation) => void;
    onCopyToClipboard?: (text: string) => void;
    restoreConversation?: (conversation: Conversation) => void;
    deleteConversationPermanently?: (conversation: Conversation) => void;
    onDuplicateItem?: (e: React.MouseEvent, conversation: Conversation) => void;
    onRemoveFromColumn?: (e: React.MouseEvent, conversation: Conversation) => void;
    onBulkRemoveFromColumn?: (conversations: ConversationDetail[]) => void;
    bulkDeleteConversations?: (conversationIds: number[]) => void;
    addToColumn?: (conversation: ConversationDetail, targetGroupId: number) => void;
    onConversationsProjectChanged?: (
      changes: Array<{ ConversationId: number; ProjectId: number; ProjectName: string }>,
    ) => void;
    onMoveFromColumn?: (
      e: React.MouseEvent,
      conversation: Conversation,
      newGroup: number,
      response: Dashboard
    ) => void;
    createConversation?: (conv: Conversation, cardType: SideMenuItem) => void;
    closeNotifications?: () => void;
    onOpenNewWindow?: (conversation: ConversationDetail) => void;
    getBaseUrl?: () => string;
  };
  onUpdateConversation?: (
    conversationId: number,
    updates: Partial<Conversation>,
    flag: boolean,
    section?: string
  ) => void;
  onMarkAsRedFlag?: (e: React.MouseEvent) => void;
  onMarkAsImportant?: (e: React.MouseEvent) => void;
  onConverToProccess?: (e: React.MouseEvent) => void;
  onConvertTo?: (conversation: Conversation) => void;
  onDuplicateItem?: (conversation: Conversation) => void;
  onSubmitTime?: (data: any) => void;
  onAddReply?: (data: any) => void;
  SocketConnectionId?: string;
  isRedFlag?: boolean;
  isImportant?: boolean;
  isConversationDetailsView?: boolean;
  isInfoWindow?: boolean;
  isInfowindowViewCard?: boolean;
  ShowMoveto?: boolean;
  HasItemCreationPermission?: boolean;
  closeOnResizeWindow?: boolean;
  configMenus?: {
    conversation: string[];
  };
  phaseSelected?: any;
  finished?: boolean;
  hideProjectName?: boolean;
  groupIsDefault: boolean | null;
  navigate?: NavigateFn;
  /** True when this card is part of a multi-card selection — shows the multi-select action menu */
  isMultiple?: boolean;
  /** Returns the full list of currently selected conversations for bulk operations */
  getSelectedConversations?: () => ConversationDetail[];
}

interface ActionMenuConversationState {
  isOpen: boolean;
  menuVisible: boolean;
  linkCopied: boolean;
  conversationDeleted: boolean;
  conversationArchive: boolean;
  infoWindowIsOpen: boolean;
  isUserFollowingConversation: number;
  canEdit: boolean;
  canDelete: boolean;
  editDetail: boolean;
  top: number;
  left: number;
  subMenuItems: MenuConfigItem[];
  showSubMenu: boolean;
  isLoadingMenu: boolean;
  modal: ModalState;
  modalEditHistory: ModalState;
  modalConvertTo: ModalState;
  modalDelete: ModalState;
  modalCopyMove: ModalState;
  modalCreatePhase: ModalState;
  modalEditMultipleCard: ModalState;
  modalChangeProject: ModalState;
  modalEditSecurityGroups: boolean;
  modalToggleUserStatus: boolean;
  modalUserIsEnabled: boolean;
  personIsEnabled: boolean | null;
  groupIsDefault: boolean | null;
  isMultiple: boolean | null;
  selectedConversations: ConversationDetail[];
}

class ActionMenuConversation extends React.Component<
  ActionMenuConversationProps,
  ActionMenuConversationState
>
{
  static defaultProps = {
    closeOnResizeWindow: false,
  };

  private actionMenuElementRef: React.RefObject<HTMLDivElement | null>;
  private corgeeSubMenuItemRef: React.RefObject<HTMLUListElement | null>;
  private menuRef: React.RefObject<HTMLUListElement | null>;
  private _buttonRect: DOMRect | null = null;

  constructor(props: ActionMenuConversationProps)
  {
    super(props);

    this.actionMenuElementRef = React.createRef();
    this.corgeeSubMenuItemRef = React.createRef();
    this.menuRef = React.createRef();

    this.state = {
      isOpen: false,
      menuVisible: false,
      linkCopied: false,
      conversationDeleted: false,
      conversationArchive: false,
      infoWindowIsOpen: false,
      isUserFollowingConversation: props.conversation.isUserFollowingConversation || 0,
      canEdit: false,
      canDelete: false,
      editDetail: false,
      top: 0,
      left: 0,
      subMenuItems: [],
      showSubMenu: false,
      isLoadingMenu: true,
      modal: {
        open: false,
        title: '',
        template: null,
      },
      modalEditHistory: {
        open: false,
        title: '',
        template: null,
      },
      modalConvertTo: {
        open: false,
        title: '',
        template: null,
      },
      modalDelete: {
        open: false,
        title: '',
        template: null,
      },
      modalCopyMove: {
        open: false,
        title: '',
        template: null,
      },
      modalCreatePhase: {
        open: false,
        title: '',
        template: null,
      },
      modalEditMultipleCard: {
        open: false,
        title: '',
        template: null,
        config: null,
      },
      modalChangeProject: {
        open: false,
        title: '',
        template: null,
      },
      modalEditSecurityGroups: false,
      modalToggleUserStatus: false,
      modalUserIsEnabled: false,
      personIsEnabled: null,
      groupIsDefault: props.groupIsDefault,
      isMultiple: false,
      selectedConversations: [],
    };

    this.onCopyToClipboard = this.onCopyToClipboard.bind(this);
    this.onOpenInfoWindow = this.onOpenInfoWindow.bind(this);
    this.onGoToProjectView = this.onGoToProjectView.bind(this);
    this.onMarkAsRedFlag = this.onMarkAsRedFlag.bind(this);
    this.onMarkAsImportant = this.onMarkAsImportant.bind(this);
    this.onFollowConversation = this.onFollowConversation.bind(this);
    this.onEdit = this.onEdit.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onArchive = this.onArchive.bind(this);
    this.onResizeWindow = this.onResizeWindow.bind(this);
    this.onOpenMenu = this.onOpenMenu.bind(this);
    this.onCopyTo = this.onCopyTo.bind(this);
    this.onMoveTo = this.onMoveTo.bind(this);
    this.onOpenDetailsNewTab = this.onOpenDetailsNewTab.bind(this);
    this.openSubMenu = this.openSubMenu.bind(this);
    this.onCreateNewItem = this.onCreateNewItem.bind(this);
    this.closeSubMenu = this.closeSubMenu.bind(this);
    this.onConvertTo = this.onConvertTo.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.openModalSubmitTime = this.openModalSubmitTime.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
    this.openModalEditHistory = this.openModalEditHistory.bind(this);
    this.onCloseModalEditHistory = this.onCloseModalEditHistory.bind(this);
    this.onDuplicateProject = this.onDuplicateProject.bind(this);
    this.onDuplicateItem = this.onDuplicateItem.bind(this);
    this.onConverToProccess = this.onConverToProccess.bind(this);
    this.onRestore = this.onRestore.bind(this);
    this.onDeletePermanently = this.onDeletePermanently.bind(this);
    this.onDeletedConversation = this.onDeletedConversation.bind(this);
    this.onArchiveConversation = this.onArchiveConversation.bind(this);
    this.onRemoveFromColumn = this.onRemoveFromColumn.bind(this);
    this.onCreatePhase = this.onCreatePhase.bind(this);
    this.onEditMultipleDetails = this.onEditMultipleDetails.bind(this);
    this.onChangeProject = this.onChangeProject.bind(this);
    this.onBulkMarkAsRedFlag = this.onBulkMarkAsRedFlag.bind(this);
    this.onBulkMarkAsImportant = this.onBulkMarkAsImportant.bind(this);
    this.onBulkUnfollow = this.onBulkUnfollow.bind(this);
    this.onBulkArchive = this.onBulkArchive.bind(this);
    this.onBulkDelete = this.onBulkDelete.bind(this);
    this.onBulkRemoveFromColumn = this.onBulkRemoveFromColumn.bind(this);
    this.onBulkMoveTo = this.onBulkMoveTo.bind(this);
    this.onBulkCopyTo = this.onBulkCopyTo.bind(this);
    this.onBulkConvertTo = this.onBulkConvertTo.bind(this);
  }

  onOpenDetailsNewTab(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;

    // Check if there's a custom onOpenNewWindow function (e.g., from chat)
    if (this.props.corgeeFunctions?.onOpenNewWindow)
    {
      this.props.corgeeFunctions.onOpenNewWindow(conversation);
      this.closeMenu();
      return;
    }

    // Default behavior for web app
    window.open(
      window.location.origin +
        import.meta.env.BASE_URL +
        'Dashboard/ConversationDetails/#/' +
        conversation.ConversationId,
      '_blank',
    );
    this.closeMenu();
  }

  onMoveTo(e: React.MouseEvent): void
  {
    e.preventDefault();

    this.setState(prev => ({
      modalCopyMove: { ...prev.modalCopyMove, title: 'Move To', open: true },
    }));

    this.closeMenu();
  }

  onCopyTo(e: React.MouseEvent): void
  {
    e.preventDefault();

    this.setState(prev => ({
      modalCopyMove: { ...prev.modalCopyMove, title: 'Copy To', open: true },
    }));

    this.closeMenu();
  }

  openModalSubmitTime(): void
  {
    this.closeMenu();
    const isPluginBeta = localStorage.getItem('C2_isSecurityGroupBeta') === 'true';
    const plugin = (window as any).CorgeePlugin;
    if (isPluginBeta && plugin)
    {
      this._openPlugin();
    }
    else
    {
      this.setState({ modal: { open: true, title: 'Time Tracking', template: null } });
    }
  }

  async _openPlugin(): Promise<void>
  {
    const { conversation, phaseSelected } = this.props;
    const personId = parseInt(authStorage.getPersonId() || '0');
    const name = authStorage.getGivenName() || authStorage.getUsername() || '';
    const plugin: any = (window as any).CorgeePlugin;

    try
    {
      // ── Fetch data FIRST so coreTaskId is available in the onComplete closure ──

      const phases: any[] = await apiService
        .get<
          any[]
        >(`Conversation/GetPhasesByConversationId?ConversationId=${conversation.ConversationId}`)
        .catch(() => []);

      // "All Phases" (ConversationPhaseId === 0) is not a real phase
      const hasPhases: boolean = conversation.HasPhases === true || phases.length > 0;
      const isRealPhase: boolean = (phaseSelected?.ConversationPhaseId ?? 0) > 0;
      const effectivePhase: any = isRealPhase ? phaseSelected : null;
      const phaseIsMissing: boolean = hasPhases && !isRealPhase;

      const coreTaskId: number =
        effectivePhase?.CoreTaskId ??
        effectivePhase?.CoreTaskID ??
        conversation.CoreTaskId ??
        0;

      const cfData: any[] = await apiService
        .get<
          any[]
        >(`CustomField/GetCustomFieldsByItemId?CoreTaskId=${coreTaskId}&IsDeleted=false&ProjectId=${conversation.ProjectId}`)
        .catch(() => []);

      const metricsData: any = await apiService
        .get<any>(
          `DailyAssetMetric/GetDailyMetricList?conversationId=${conversation.ConversationId}`,
        )
        .catch(() => ({ metricList: [] }));
      const metrics: any[] = metricsData?.metricList ?? [];

      const groupedMetrics = metrics.reduce((acc: any[], m: any) =>
      {
        if (m.PhaseId !== coreTaskId) return acc;
        const existing = acc.find((x: any) => x.LayerId === m.LayerId);
        if (existing) existing.ProducedHours += m.ProducedHours;
        else acc.push({ LayerId: m.LayerId, ProducedHours: m.ProducedHours });
        return acc;
      }, []);

      const layers: any[] = [];
      for (const layerObj of cfData)
      {
        const assignedTo = layerObj.CustomFields?.find((x: any) => x.Name === 'Assigned to');
        const estimate = layerObj.CustomFields?.find((x: any) => x.Name === 'Estimated');
        const remaining = layerObj.CustomFields?.find((x: any) => x.Name === 'Remaining');
        const actual = layerObj.CustomFields?.find((x: any) => x.Name === 'Actual');
        const approver = layerObj.CustomFields?.find(
          (x: any) => x.Name === 'Approver' || x.Name === 'Approveer',
        );
        const dueDate = layerObj.CustomFields?.find((x: any) => x.Name === 'Due date');
        const produced = groupedMetrics.find((x: any) => x.LayerId === layerObj.LayerId);

        const isAssigned =
          assignedTo?.Value != null &&
          String(assignedTo.Value)
            .split(',')
            .some((x: string) => x.trim() === String(personId));

        if (isAssigned && estimate?.Value != null && remaining?.Value != null)
        {
          layers.push({
            id: layerObj.LayerId,
            name: layerObj.Name,
            estimate: estimate.Value,
            estimateId: estimate.CustomFieldId,
            remaining: remaining.Value,
            remainingId: remaining.CustomFieldId,
            actual: actual?.Value ?? '0',
            actualId: actual?.CustomFieldId ?? 0,
            approver: approver?.SelectedValuesText ?? 'Not Assigned',
            dueDate: dueDate?.Value ?? null,
            producedHours: produced?.ProducedHours ?? 0,
          });
        }
      }

      // ── Register onComplete after data fetch — coreTaskId is now in closure ──
      plugin.initOrUpdate({
        onComplete: async (data: any) =>
        {
          // Ensure all nested objects exist to prevent NullReferenceException in C# controller
          data.calculated = data.calculated ?? { producedHours: 0 };
          data.rework = data.rework ?? { didRework: false, reworkHours: 0, reworkType: '' };
          data.feedbackLoop = data.feedbackLoop ?? {
            hadReview: false,
            reviewer: '',
            reviewResult: '',
          };
          data.impediments = data.impediments ?? {
            hadImpediments: false,
            impedimentHours: 0,
            impedimentType: '',
          };
          data.process = data.process ?? {
            completedPost: false,
            usedXref: false,
            validatedAdc: false,
          };
          data.requirementChanges = data.requirementChanges ?? { hadChanges: false, severity: '' };
          data.status = data.status ?? { status: '' };
          data.notes = data.notes ?? { note: '' };
          if (data.meta)
          {
            data.meta.phase = data.meta.phase ?? {
              ConversationPhaseId: 0,
              CoreTaskId: coreTaskId,
              Title: '',
            };
          }
          // Keep as string with 2 decimals — same as legacy (.toFixed(2))
          if (data.progress)
          {
            data.progress.estimatedHoursRemaining = parseFloat(
              data.progress.estimatedHoursRemaining,
            ).toFixed(2);
          }

          // Convert decimal hours to "HH:MM" — C# TimeSpan.Parse() requires this format
          const rawHours = parseFloat(data.actual?.hoursWorked ?? 0);
          const hh = Math.floor(rawHours);
          const mm = Math.round((rawHours - hh) * 60);
          const hoursStr = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
          const currentDate = new Date(data.meta?.date ?? new Date());
          const note = data.notes?.note ?? '';
          const remainingField = '' + data.progress?.estimatedHoursRemaining;
          const actualField = '' + data.meta?.actual;

          try
          {
            // Step 1: Insert daily asset metric — capture the returned ID
            const metricResponse: any = await apiService.post(
              'DailyAssetMetric/InsertDailyAssetMetric',
              data,
            );
            const dailyAssetMetricId: number =
              metricResponse?.DailyAssetMetricId ?? metricResponse?.Id ?? 0;

            // Step 2: Create submit-time reply to get ReplyId (mirrors legacy AddSubmitTimeReply)
            const formattedDate = currentDate.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            });
            const bodyHtml =
              `<div><strong>Posted from submit time: ${formattedDate}</strong>` +
              `<p><br/><strong>Logged Time:</strong> ${hoursStr}` +
              `&nbsp;&nbsp;&nbsp;<strong>Remaining:</strong> ${remainingField.includes('.') ? remainingField + '0' : remainingField + '.00'}` +
              `&nbsp;&nbsp;&nbsp;<strong>Actual:</strong> ${actualField.includes('.') ? actualField : actualField + '.0'}` +
              `</p><br/>${note}</div>`;

            const selectedPhase = data.meta?.phase ?? effectivePhase;
            const replyItem: any = {
              ReplyId: 0,
              ConversationId: conversation.ConversationId,
              ReplyLayers: [
                {
                  LayerId: data.meta?.layerId ?? 0,
                  LayerColor: '#F222F3',
                  LayerName: data.meta?.layerName ?? '',
                  ReplyId: 0,
                },
              ],
              Attachments: [],
              Body: bodyHtml,
              Tags: [],
              Socketconnectionid: '',
              possibleTags: [],
              type: 0,
              ConversationPhaseId: selectedPhase?.ConversationPhaseId ?? 0,
              PhaseName: selectedPhase?.Title ?? '',
            };

            let replyId = 0;
            let replyResponse: any = null;
            try
            {
              replyResponse = await apiService.post('reply', replyItem);
              replyId = replyResponse?.ReplyId ?? 0;
            }
            catch (replyErr)
            {
              console.error('Error creating submit time reply', replyErr);
            }

            // Step 3: Create TaskTimeTracking — updates Remaining/Actual custom fields on the card
            const requestParams: any = {
              TimeTrackingID: 0,
              ConversationId: conversation.ConversationId,
              ConfigValueID: conversation.ConfigValueId,
              CoreTaskID: data.meta?.phase?.CoreTaskId ?? coreTaskId,
              LayerId: data.meta?.layerId ?? 0,
              ProjectID: conversation.ProjectId,
              CustomFieldId: data.meta?.remainingId ?? 0,
              CustomFieldValue: remainingField,
              CustomFieldActualId: data.meta?.actualId ?? 0,
              CustomFieldActualValue: actualField,
              Discriminator: 'CoreTaskCustomFieldValue',
              Date: currentDate.toISOString().replace('Z', ''),
              Description: note,
              Hours: hoursStr,
              Socketconnectionid: '',
              warningEstimated: '' + data.meta?.estimate,
              TeamId: data.meta?.teamId ?? null,
              ReplyId: replyId,
            };

            const trackingResponse: any = await apiService.post('TaskTimeTracking', requestParams);
            const taskTimeTrackingId: number =
              trackingResponse?.Id ?? trackingResponse?.TimeTrackingID ?? 0;

            // Step 4: Link DailyAssetMetric ↔ TaskTimeTracking
            if (dailyAssetMetricId > 0 && taskTimeTrackingId > 0)
            {
              await apiService
                .put(
                  `DailyAssetMetric/UpdateMetricTimeTracking?assetMetricId=${dailyAssetMetricId}&timeTrackingId=${taskTimeTrackingId}`,
                  {},
                )
                .catch(() => {});
            }

            // Step 5: Notify the InfoWindow (and ConversationDetailsView) so the new
            // reply appears immediately — same as what SubmitTime.tsx does for the
            // regular modal path
            if (replyResponse && replyId > 0)
            {
              if (selectedPhase)
              {
                replyResponse.ConversationPhaseId = selectedPhase.ConversationPhaseId ?? 0;
                replyResponse.PhaseName = selectedPhase.Title ?? '';
              }
              conversationBus.emit('reply-added', {
                conversationId: conversation.ConversationId,
                reply: replyResponse,
              });
            }

            plugin.showSuccess();
            // Re-validate so cards unblock immediately if hours are now ≥ 8
            validateYesterdayMetrics();
          }
          catch (e)
          {
            console.error('Error saving time tracking', e);
          }
        },
        onClose: () => {},
      });

      // ── Open the plugin ──
      plugin.open({
        userId: personId,
        name,
        lead: 'Not Assigned',
        cardId: conversation.ConversationId,
        projectId: conversation.ProjectId,
        // Without a real phase the plugin must ask the user to pick one
        layers: phaseIsMissing || layers.length === 0 ? null : layers,
        phase: effectivePhase,
      });
    }
    catch (e)
    {
      console.error('Error opening CorgeePlugin', e);
    }
  }

  openModalEditHistory(): void
  {
    this.closeMenu();
    this.setState({
      modalEditHistory: { open: true, title: 'Edit Detail History', template: null },
    });
  }

  onCloseModalEditHistory(_e?: React.MouseEvent): void
  {
    this.setState({ modalEditHistory: { open: false, title: '', template: null } });
  }

  onCloseModal(_e?: React.MouseEvent): void
  {
    this.setState({ modal: { open: false, title: '', template: null } });
    // Re-validate so cards unblock immediately if the user just submitted ≥ 8 hours
    validateYesterdayMetrics();
  }

  onOpenMenu(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to DashboardGroups container which would clear multi-selection
    const elem = e.currentTarget as HTMLElement;
    const actionMenuElement = $(elem.closest('#actionMenuElement') as any);

    if (!this.state.isOpen)
    {
      const { api, conversation } = this.props;

      // From state, not the DOM: virtualized columns drop the cards scrolled out of view
      const isEffectivelyMultiple =
        getSelectedConversationCount(this.props.getSelectedConversations) > 1;

      // Capture selected conversations once when the menu opens
      const selectedConvs =
        isEffectivelyMultiple && this.props.getSelectedConversations
          ? this.props.getSelectedConversations()
          : [this.props.conversation];
      this.setState({ selectedConversations: selectedConvs });

      const _canEdit = CorgeeHelper.convHasEditPermissions(this.props.conversation, null);
      const _canDelete = CorgeeHelper.convHasDeletePermissions(this.props.conversation);
      const _canEditDetail = CorgeeHelper.canEditEditDetailHistory(this.props.conversation);

      // Capture button rect and calculate horizontal position immediately
      const rect = elem.getBoundingClientRect();
      this._buttonRect = rect;

      const dropdownW = 200;
      // Try to open to the right of the button; fall back to the left if it would go off-screen
      let newLeft = rect.right;
      if (newLeft + dropdownW > window.innerWidth)
      {
        newLeft = rect.right - dropdownW;
      }
      if (newLeft < 0) newLeft = 4;

      this.setState({
        isOpen: true,
        menuVisible: false,
        canEdit: _canEdit,
        canDelete: _canDelete,
        top: 0,
        left: newLeft,
        editDetail: _canEditDetail,
        isLoadingMenu: true,
        isMultiple: isEffectivelyMultiple,
      });

      // After render, measure the real menu dimensions and position
      requestAnimationFrame(() =>
      {
        const menuEl = this.menuRef.current;
        const btnRect = this._buttonRect;
        if (!menuEl || !btnRect || !this.state.isOpen) return;

        const menuHeight = menuEl.offsetHeight;
        const menuWidth = menuEl.offsetWidth;
        const spaceAbove = btnRect.top;
        const spaceBelow = window.innerHeight - btnRect.bottom;

        let newTop: number;
        if (spaceAbove >= menuHeight)
        {
          // Enough space above: open upward
          newTop = btnRect.top - menuHeight;
        }
        else if (spaceBelow >= menuHeight)
        {
          // Enough space below: open downward
          newTop = btnRect.bottom + 2;
        }
        else
        {
          // Neither side fits fully: use whichever has more space
          newTop =
            spaceAbove >= spaceBelow ? Math.max(4, btnRect.top - menuHeight) : btnRect.bottom + 2;
        }

        // Clamp vertically to viewport
        if (newTop < 4) newTop = 4;
        if (newTop + menuHeight > window.innerHeight)
        {
          newTop = Math.max(4, window.innerHeight - menuHeight - 4);
        }

        // Clamp horizontally: if menu overflows the right edge, shift left
        let correctedLeft = newLeft;
        if (correctedLeft + menuWidth > window.innerWidth)
        {
          correctedLeft = Math.max(4, window.innerWidth - menuWidth - 4);
        }

        this._buttonRect = null;
        this.setState({ top: newTop, left: correctedLeft, menuVisible: true });
      });

      setTimeout(() =>
      {
        const conversationInfoWindow = parseInt(
          sessionStorage.getItem('C2_conversationInfoWindow') || '0',
        );
        if (conversationInfoWindow === this.props.conversation.ConversationId)
        {
          this.setState({ infoWindowIsOpen: true });
        }
        else
        {
          this.setState({ infoWindowIsOpen: false });
        }

        if (this.props.isInfoWindow)
        {
          this.setState({ infoWindowIsOpen: true });
        }

        const groupContainer = (actionMenuElement as any).closest('.dashboardGroupSelector')[0];
        const initialScroll = $(groupContainer).scrollTop() || 0;
        const rf = this;

        function watchScroll(e: Event)
        {
          const finalScroll = $(groupContainer).scrollTop() || 0;
          if (initialScroll !== finalScroll)
          {
            rf.setState({ isOpen: false });
            (e.currentTarget as HTMLElement)?.removeEventListener('scroll', watchScroll);
          }
        }

        if (groupContainer)
        {
          groupContainer.addEventListener('scroll', watchScroll);
        }
      });

      if (conversation.ConfigValueId === 15)
      {
        const personId = conversation.PersonId ?? 0;
        if (personId)
        {
          conversationService
            .getPersonEnabledStatus(personId)
            .then(isEnabled =>
            {
              this.setState({ personIsEnabled: isEnabled });
            })
            .catch(() =>
            {
              this.setState({ personIsEnabled: conversation.IsEnable ?? false });
            });
        }
        else
        {
          this.setState({ personIsEnabled: conversation.IsEnable ?? false });
        }
      }

      MenuApi.getByConfigValuePerson(api, conversation.ProjectId)
        .then(response =>
        {
          ConversationApi.GetNotificationTypeByPerson(api, conversation.ConversationId)
            .then(type =>
            {
              const subMenuItems = response.filter((x: MenuConfigItem) =>
              {
                return x.ConfigValueID != 1 && x.ConfigValueID != 9 && x.ConfigValueID != 15;
              });
              this.setState({ isUserFollowingConversation: type.length > 0 ? 1 : 0 });
              this.setState({ isLoadingMenu: false });
              this.setState({ subMenuItems: subMenuItems });
            })
            .catch(error =>
            {
              console.error(error);
            });
        })
        .catch(error =>
        {
          console.error(error);
        });
    }
  }

  onEdit(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.editConversation)
    {
      const convToEdit: any = { ...this.props.conversation };
      if ((this.props as any).phaseSelected)
      {
        convToEdit.phaseSelected = (this.props as any).phaseSelected;
      }
      this.props.corgeeFunctions.editConversation(e, convToEdit);
    }
    this.closeMenu();
  }

  onDelete(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    this.setState(prev => ({ modalDelete: { ...prev.modalDelete, open: true } }));
    this.closeMenu();
  }

  async onArchive(e: React.MouseEvent): Promise<void>
  {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.archiveConversation)
    {
      const dashboardGroupConvs = [this.props.conversation].map(conv =>
      {
        return {
          PersonId: CorgeeHelper.getPersonId(),
          ConversationId: conv.ConversationId,
          ProjectId: conv.ProjectId,
        };
      });
      await conversationService.ArchivedConversations(dashboardGroupConvs);
      this.props.corgeeFunctions &&
        this.props.corgeeFunctions.deleteConversation &&
        this.props.corgeeFunctions.deleteConversation(null as any, this.props.conversation);
      this.onArchiveConversation();
    }
    this.closeMenu();
  }

  onCreatePhase(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    const selectedConversations =
      this.state.selectedConversations.length > 0
        ? this.state.selectedConversations
        : [this.props.conversation];
    this.setState(prev => ({
      modalCreatePhase: { ...prev.modalCreatePhase, open: true, config: selectedConversations },
    }));
    this.closeMenu();
  }

  onEditMultipleDetails(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();

    const selectedIds = getSelectedConversationIds(
      this.props.getSelectedConversations,
      this.props.conversation.ConversationId,
    );

    const config = {
      api: {
        url: this.props.api.url,
        token: this.props.api.token,
        TenancyName: authStorage.getTenantData(),
        PersonId: authStorage.getPersonId(),
        SocketServer: (this.props.SocketConnectionId || '') + '/usersNotificationManager',
      },
      ConversationIds: selectedIds,
      SelectedConversations: getSelectedConversationTitles(
        this.props.getSelectedConversations,
        this.props.conversation,
      ),
      conversation: this.props.conversation,
      DashboardId: String(this.props.conversation.DashboardId ?? ''),
      openmodal: true,
      hasmaxheight: true,
      isdraggable: true,
      closeonescape: true,
      allowMinimize: false,
      allowMaximize: false,
      title: 'Edit multiple cards details',
      disableAutoSave: true,
      canEdit: true,
      functions: {
        getSocketConnectionId: () => this.props.SocketConnectionId ?? '',
        reloadConversations: (_data: any, updatedConv: any) =>
        {
          selectedIds.forEach((id: string) =>
          {
            const convId = Number(id);

            if (this.props.onUpdateConversation)
            {
              const updatePayload: Record<string, any> = {};
              if (updatedConv?.ConversationLayers !== undefined)
              {
                updatePayload.ConversationLayers = updatedConv.ConversationLayers;
              }
              if (updatedConv?.OwnedBy != null && updatedConv.OwnedBy !== '')
              {
                updatePayload.OwnedBy = updatedConv.OwnedBy;
                updatePayload.OwnedById = updatedConv.OwnedById;
              }
              if (Object.keys(updatePayload).length > 0)
              {
                this.props.onUpdateConversation(convId, updatePayload as any, false);
              }
            }

            conversationBus.emit('layer-updated', {
              conversationId: convId,
              layerId: 0,
              layerData: updatedConv?.ConversationLayers ?? {},
              sourceId: 'multi-edit', // Triggers full reload in InfoWindow
              // TODO: Add coreTaskId/conversationPhaseId if multi-edit supports phases
            });
          });
        },
      },
    };
    this.setState(prev => ({
      modalEditMultipleCard: { ...prev.modalEditMultipleCard, open: true, config },
    }));
    this.closeMenu();
  }

  onChangeProject(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    this.setState(prev => ({ modalChangeProject: { ...prev.modalChangeProject, open: true } }));
    this.closeMenu();
  }

  // ─── Bulk / multi-select handlers ─────────────────────────────────────────

  onBulkMarkAsRedFlag(e: React.MouseEvent): void
  {
    e.preventDefault();
    const convs = this.state.selectedConversations;
    if (!convs.length) return;
    const isRedFlag = !convs[0]!.IsRedFlag;
    const requestParams = {
      conversations: convs.map(c => ({
        ConfigValueId: c.ConfigValueId,
        ConversationId: c.ConversationId,
        CoreTaskId: c.CoreTaskId,
        CreatedByID: c.CreatedByID,
        Finished: c.Finished,
        Icon: c.Icon,
        IsRedFlag: isRedFlag,
        ProjectId: c.ProjectId,
        SideMenuText: c.SideMenuText,
        Title: c.Title,
        UpdatedAt: c.UpdatedAt,
        callService: true,
      })),
      SocketConnectionId: this.props.SocketConnectionId || '',
    };
    ConversationApi.updateRedFlags(this.props.api, requestParams).catch(console.error);
    this.closeMenu();
  }

  onBulkMarkAsImportant(e: React.MouseEvent): void
  {
    e.preventDefault();
    const convs = this.state.selectedConversations;
    if (!convs.length) return;
    const isImportant = !convs[0]!.IsImportant;
    const requestParams = {
      conversations: convs.map(c => ({
        ConfigValueId: c.ConfigValueId,
        ConversationId: c.ConversationId,
        CoreTaskId: c.CoreTaskId,
        CreatedByID: c.CreatedByID,
        Finished: c.Finished,
        Icon: c.Icon,
        IsImportant: isImportant,
        ProjectId: c.ProjectId,
        SideMenuText: c.SideMenuText,
        Title: c.Title,
        UpdatedAt: c.UpdatedAt,
        callService: true,
      })),
      SocketConnectionId: this.props.SocketConnectionId || '',
    };
    ConversationApi.updateImportant(this.props.api, requestParams).catch(console.error);
    this.closeMenu();
  }

  onBulkUnfollow(e: React.MouseEvent): void
  {
    e.preventDefault();
    const convs = this.state.selectedConversations;
    if (!convs.length) return;
    const requestParams = {
      conversationsFollowers: convs.map(c => ({
        ConversationId: c.ConversationId,
        Delete: 1,
      })),
    };
    ConversationApi.saveFollower(this.props.api, requestParams).catch(console.error);
    this.closeMenu();
  }

  async onBulkArchive(e: React.MouseEvent): Promise<void>
  {
    e.preventDefault();
    e.stopPropagation();
    const convs = this.state.selectedConversations;
    if (!convs.length) return;
    const isUnarchive = convs.every(c => c.IsArchived);
    const dashboardGroupConvs = convs.map(c => ({
      PersonId: CorgeeHelper.getPersonId(),
      ConversationId: c.ConversationId,
      ProjectId: c.ProjectId,
    }));
    await conversationService.ArchivedConversations(dashboardGroupConvs);
    if (isUnarchive)
    {
      convs.forEach(c =>
      {
        this.props.onUpdateConversation?.(
          c.ConversationId,
          { IsArchived: false } as any,
          true,
          'header',
        );
      });
    }
    else
    {
      convs.forEach(c =>
      {
        this.props.corgeeFunctions?.archiveConversation?.(null as any, c as Conversation);
      });
      this.onArchiveConversation();
    }
    this.closeMenu();
  }

  onBulkDelete(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    this.setState(prev => ({ modalDelete: { ...prev.modalDelete, open: true } }));
    this.closeMenu();
  }

  onBulkRemoveFromColumn(e: React.MouseEvent): void
  {
    e.preventDefault();
    const convs = this.state.selectedConversations;
    convs.forEach(c =>
    {
      this.props.corgeeFunctions?.onRemoveFromColumn &&
        this.props.corgeeFunctions.onRemoveFromColumn(e, c as Conversation);
    });
    this.closeMenu();
  }

  onBulkMoveTo(e: React.MouseEvent): void
  {
    e.preventDefault();
    this.setState(prev => ({
      modalCopyMove: { ...prev.modalCopyMove, title: 'Move To', open: true },
    }));
    this.closeMenu();
  }

  onBulkCopyTo(e: React.MouseEvent): void
  {
    e.preventDefault();
    this.setState(prev => ({
      modalCopyMove: { ...prev.modalCopyMove, title: 'Copy To', open: true },
    }));
    this.closeMenu();
  }

  onBulkConvertTo(): void
  {
    this.setState(prev => ({
      modalConvertTo: { ...prev.modalConvertTo, title: 'Convert To', open: true },
    }));
    this.closeMenu();
  }

  // ──────────────────────────────────────────────────────────────────────────

  onFollowConversation(e: React.MouseEvent): void
  {
    e.preventDefault();

    const { conversation } = this.props;

    let newIsUserFollowingConversation = 0;
    let deleteFolow = 0;
    if (this.state.isUserFollowingConversation === 1)
    {
      newIsUserFollowingConversation = 1;
      deleteFolow = 1;
    }

    const requestParams = {
      conversationsFollowers: [
        {
          ConversationId: conversation.ConversationId,
          Delete: deleteFolow,
        },
      ],
    };

    ConversationApi.saveFollower(this.props.api, requestParams)
      .then(_response =>
      {
        this.setState({ isUserFollowingConversation: newIsUserFollowingConversation });

        if (this.props.onUpdateConversation)
        {
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            { isUserFollowingConversation: newIsUserFollowingConversation } as any,
            true,
            'header',
          );
        }

        ConversationApi.SetConversationsAsViewed(this.props.api, {
          ConversationIds: String(this.props.conversation.ConversationId),
        }).then(_resp => {});
      })
      .catch(error =>
      {
        console.error(error);
      });
    this.closeMenu();
  }

  onGoToProjectView(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;

    // Check if there's a custom goToProject function (e.g., from chat)
    if (this.props.corgeeFunctions?.goToProject)
    {
      this.props.corgeeFunctions.goToProject(e, conversation);
      this.closeMenu();
      return;
    }

    // Default behavior for web app
    const path = `/ProjectDetails/${conversation.ProjectId}`;
    if (e.shiftKey)
    {
      window.open(window.location.origin + path, '_blank');
    }
    else if (e.ctrlKey || e.metaKey)
    {
      window.open(window.location.origin + path, '_blank');
    }
    else
    {
      this.props.navigate?.(path);
    }
    this.closeMenu();
  }

  onMarkAsRedFlag(e: React.MouseEvent): void
  {
    e.preventDefault();

    if (this.props.onMarkAsRedFlag)
    {
      this.props.onMarkAsRedFlag(e);
    }
    this.closeMenu();
  }

  onMarkAsImportant(e: React.MouseEvent): void
  {
    e.preventDefault();
    if (this.props.onMarkAsImportant)
    {
      this.props.onMarkAsImportant(e);
    }
    this.closeMenu();
  }

  onConverToProccess(response: ConversionResult[]): void
  {
    if (this.props.onConverToProccess)
    {
      this.props.onConverToProccess(response as any);
    }
  }

  onOpenInfoWindow(e: React.MouseEvent): void
  {
    e.preventDefault();

    if (this.state.infoWindowIsOpen || this.props.isInfowindowViewCard)
    {
      if (this.props.corgeeFunctions && this.props.corgeeFunctions.onCloseInfoWindow)
      {
        this.props.corgeeFunctions.onCloseInfoWindow(this.props.conversation);
      }
    }
    else
    {
      if (this.props.corgeeFunctions && this.props.corgeeFunctions.openInfoWindow)
      {
        this.props.corgeeFunctions.openInfoWindow(this.props.conversation);
      }
    }
    this.closeMenu();
  }

  onDuplicateProject(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;

    if (this.props.corgeeFunctions && this.props.corgeeFunctions.onDuplicateProject)
    {
      this.props.corgeeFunctions.onDuplicateProject(e, conversation);
    }

    this.closeMenu();
  }

  onRemoveFromColumn(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;

    if (this.props.corgeeFunctions && this.props.corgeeFunctions.onRemoveFromColumn)
    {
      this.props.corgeeFunctions.onRemoveFromColumn(e, conversation);
    }

    this.closeMenu();
  }

  onCopyToClipboard(): void
  {
    const { conversation, corgeeFunctions } = this.props;

    // In host apps (Chat) getBaseUrl() is the full web base; don't append BASE_URL (the
    // host's own /chatv2/). Web falls back to window.location.origin + BASE_URL.
    const customBaseUrl = corgeeFunctions?.getBaseUrl?.();

    const textToCopy = customBaseUrl
      ? customBaseUrl.replace(/\/+$/, '') +
        '/Dashboard/ConversationDetails/#/' +
        conversation.ConversationId
      : window.location.origin +
        import.meta.env.BASE_URL +
        'Dashboard/ConversationDetails/#/' +
        conversation.ConversationId;

    navigator.clipboard
      .writeText(textToCopy)
      .then(() =>
      {
        this.setState({ linkCopied: true }, () =>
        {
          setTimeout(() =>
          {
            $('.corgee_com_notification-bubble-container.clipboard-notification-r')
              .fadeIn()
              .delay(1000)
              .fadeOut(3000);

            if (this.props.corgeeFunctions && this.props.corgeeFunctions.onCopyToClipboard)
            {
              this.props.corgeeFunctions.onCopyToClipboard(textToCopy);
            }
            this.closeMenu();

            setTimeout(() =>
            {
              this.setState({ linkCopied: false });
            }, 4000);
          });
        });
      })
      .catch(err =>
      {
        console.error('Failed to copy: ', err);
        this.closeMenu();
      });
  }

  onResizeWindow(e: Event): void
  {
    e.preventDefault();
    this.setState({ isOpen: false });
  }

  componentDidMount(): void
  {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps: ActionMenuConversationProps): void
  {
    // Keep selectedConversations in sync so the count badge is accurate before the menu opens
    if (prevProps.isMultiple !== this.props.isMultiple)
    {
      // Do not reset while any modal is open — selectedConversations was captured when the
      // menu opened and must remain intact until the modal action completes.
      const anyModalOpen =
        this.state.modal.open ||
        this.state.modalEditHistory.open ||
        this.state.modalConvertTo.open ||
        this.state.modalDelete.open ||
        this.state.modalCopyMove.open ||
        this.state.modalCreatePhase.open ||
        this.state.modalEditMultipleCard.open ||
        this.state.modalChangeProject.open;
      if (anyModalOpen) return;

      if (this.props.isMultiple && this.props.getSelectedConversations)
      {
        this.setState({ selectedConversations: this.props.getSelectedConversations() });
      }
      else if (!this.props.isMultiple)
      {
        this.setState({ selectedConversations: [] });
      }
    }
  }

  componentWillUnmount(): void
  {
    window.removeEventListener('scroll', this.handleScroll);
    if ($('#actionMenuElement').length > 0)
    {
      $('#actionMenuElement').off('hide.bs.dropdown');
    }
  }

  handleScroll(): void
  {
    if (
      this.actionMenuElementRef.current &&
      $(this.actionMenuElementRef.current).hasClass('open') &&
      !this.props.isInfowindowViewCard
    )
    {
      $(this.actionMenuElementRef.current).removeClass('open');
      this.setState({ isOpen: false });
    }

    if (!this.props.isInfowindowViewCard && $('.corgee_com_card-action-submenu').length > 0)
    {
      this.setState({ isOpen: false });
      this.setState({ showSubMenu: false });
    }
  }

  openSubMenu(e: React.MouseEvent): void
  {
    const el = $(e.currentTarget as any);
    this.setState({ showSubMenu: true }, () =>
    {
      setTimeout(() =>
      {
        const containerElement = $(this.corgeeSubMenuItemRef.current as any);
        const scrollWindows = window.scrollY;
        const containerMenu = el;

        let targetTopValue = containerMenu.offset()?.top || 0;
        let leftPos = (containerMenu.offset()?.left || 0) + (containerMenu.width() as number) + 16;

        const objPosW =
          (containerMenu.offset()?.left || 0) +
          (containerMenu.width() as number) +
          (containerElement.width() as number);

        if (objPosW >= ($(window).width() as number))
        {
          leftPos = (containerMenu.offset()?.left || 0) - (containerElement.width() as number) - 3;
        }
        targetTopValue = targetTopValue - scrollWindows;
        containerElement.attr('style', 'top: ' + targetTopValue + 'px; left: ' + leftPos + 'px;');
        containerElement.show();
      });
    });
  }

  closeSubMenu(): void
  {
    const containerElement = $(this.corgeeSubMenuItemRef.current as any);
    containerElement.show();
    this.setState({ showSubMenu: false });
  }

  closeMenu(): void
  {
    this.setState({
      isOpen: false,
      menuVisible: false,
      showSubMenu: false,
      isMultiple: null,
      personIsEnabled: null,
    });

    // Close notifications window in chat if it's open
    if (this.props.corgeeFunctions?.closeNotifications)
    {
      this.props.corgeeFunctions.closeNotifications();
    }
  }

  onCreateNewItem(item: MenuConfigItem): void
  {
    if (item)
    {
      const itemWithSocket = {
        ...item,
        SocketConnectionId:
          this.props.SocketConnectionId === undefined
            ? item.SocketConnectionId
            : this.props.SocketConnectionId,
      };
      this.closeMenu();
      if (this.props.corgeeFunctions?.createConversation)
      {
        this.props.corgeeFunctions?.createConversation(this.props.conversation, itemWithSocket);
      }
    }
    this.closeMenu();
    this.closeSubMenu();
  }

  onConvertTo(): void
  {
    if (this.props.onConvertTo)
    {
      this.props.onConvertTo(this.props.conversation);
    }
    this.setState(prev => ({
      modalConvertTo: { ...prev.modalConvertTo, title: 'Convert To', open: true },
    }));
    this.closeMenu();
  }

  onDuplicateItem(e: React.MouseEvent): void
  {
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.onDuplicateItem)
    {
      this.props.corgeeFunctions.onDuplicateItem(e, this.props.conversation);
    }
    this.closeMenu();
  }

  onRestore(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.restoreConversation)
    {
      this.props.corgeeFunctions.restoreConversation(this.props.conversation);
    }
    this.closeMenu();
  }

  onDeletePermanently(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.deleteConversationPermanently)
    {
      this.props.corgeeFunctions.deleteConversationPermanently(this.props.conversation);
    }
    this.closeMenu();
  }

  onDeletedConversation(): void
  {
    this.setState({ conversationDeleted: true }, () =>
    {
      setTimeout(() =>
      {
        $('.corgee_com_notification-bubble-container.deleted-notification-r')
          .fadeIn()
          .delay(1000)
          .fadeOut(3000);
        setTimeout(() =>
        {
          this.setState({ conversationDeleted: false });
        }, 4000);
      });
    });
  }

  onArchiveConversation(): void
  {
    this.setState({ conversationArchive: true }, () =>
    {
      setTimeout(() =>
      {
        $('.corgee_com_notification-bubble-container.archive-notification-r')
          .fadeIn()
          .delay(1000)
          .fadeOut(3000);
        setTimeout(() =>
        {
          this.setState({ conversationArchive: false });
        }, 4000);
      });
    });
  }

  render(): React.ReactElement
  {
    const {
      conversation,
      isRedFlag,
      isConversationDetailsView,
      configMenus,
      isInfowindowViewCard,
      api,
      ShowMoveto,
      isImportant,
      HasItemCreationPermission,
    } = this.props;
    const {
      isLoadingMenu,
      isOpen,
      menuVisible,
      linkCopied,
      conversationDeleted,
      conversationArchive,
      infoWindowIsOpen,
      isUserFollowingConversation,
      canEdit,
      canDelete,
      top,
      left,
      subMenuItems,
      showSubMenu,
      modal,
      modalEditHistory,
      editDetail,
      modalConvertTo,
      modalDelete,
      modalCopyMove,
      groupIsDefault,
      modalCreatePhase,
      modalEditMultipleCard,
      selectedConversations,
    } = this.state;

    // When the menu is open, use the value captured at open time (state) so that
    // a transient selection change while the menu is visible doesn't switch modes.
    // When the menu is closed, follow the prop to keep the button appearance correct.
    const isMultiple = this.state.isOpen
      ? (this.state.isMultiple ?? this.props.isMultiple ?? false)
      : (this.props.isMultiple ?? this.state.isMultiple ?? false);

    const isProjectCard = conversation.ConfigValueId == 9 ? true : false;
    const isPersonCard = conversation.ConfigValueId == 15 ? true : false;
    const effectiveIsEnable = isPersonCard
      ? (this.state.personIsEnabled ?? conversation.IsEnable)
      : conversation.IsEnable;
    // Hide "Move To" in host apps like Chat (no target dashboardId available)
    const isHostApp = !!this.props.corgeeFunctions?.getBaseUrl;
    const showMoveto = isHostApp ? false : ShowMoveto ? ShowMoveto : false;

    let valueIsRedFlag = conversation.IsRedFlag;
    if (isRedFlag !== undefined)
    {
      valueIsRedFlag = isRedFlag;
    }

    let valueIsImportant = conversation.IsImportant;
    if (isImportant !== undefined)
    {
      valueIsImportant = isImportant;
    }

    let existsInvalidItems = false;
    if (!isPersonCard)
    {
      if (canEdit && CorgeeHelper.getPersonId() !== conversation.CreatedByID)
      {
        existsInvalidItems = true;
      }
      if (CorgeeHelper.checkAdminPermissions())
      {
        existsInvalidItems = false;
      }
    }

    const menusConv = configMenus ? configMenus.conversation : [];

    // When cards are blocked (yesterday's hours < 8), only Submit Time stays active
    const cardsDisabled = computeCardsDisabled();

    const projectDetailsMatch =
      window.location.hash.match(/\/ProjectDetails\/(\d+)/i) ||
      window.location.href.match(/\/ProjectDetails\/(\d+)/i);
    const isInsideItsProject =
      projectDetailsMatch && projectDetailsMatch[1] === String(conversation.ProjectId);
    const shouldShowProjectName =
      !this.props.hideProjectName &&
      !isInsideItsProject &&
      conversation.ProjectId &&
      conversation.ConfigValueId;

    return (
      <div>
        <div
          id="actionMenuElement"
          ref={this.actionMenuElementRef}
          tabIndex={0}
          className="corgee_com_card-action-menu-group dropdown open"
        >
          <button
            className={`corgee_com_card-action-menu-toggle dropdown-toggle${isMultiple ? ' corgee_com_card-action-menu-toggle--multi' : ''}`}
            data-toggle="dropdown"
            onMouseDown={e => e.stopPropagation()}
            onClick={this.onOpenMenu}
            title={
              isMultiple ? `${selectedConversations.length || ''} items selected`.trim() : undefined
            }
          >
            <div className="corgee_com_card-action-menu-icon"></div>
          </button>
          {isOpen === true && (
            <RenderInBody>
              <ul
                ref={this.menuRef}
                className={`corgee_com_card-action-header-menu dropdown-menu cancel-drag${!isMultiple && cardsDisabled ? ' action-menu-blocked' : ''}`}
                style={{
                  left: left,
                  top: top,
                  position: 'fixed',
                  visibility: menuVisible ? 'visible' : 'hidden',
                }}
              >
                {/* ─── MULTI-SELECT MENU (config-driven) ─── */}
                {isMultiple &&
                  (() =>
                  {
                    const convs =
                      selectedConversations.length > 0 ? selectedConversations : [conversation];
                    const ctx: ActionMenuContext = {
                      conversation,
                      selectedConversations: convs,
                      isMultiple: true,
                      api,
                      SocketConnectionId: this.props.SocketConnectionId,
                      corgeeFunctions: this.props.corgeeFunctions,
                      groupIsDefault,
                      openModal: {
                        delete: () =>
                          this.setState(prev => ({
                            modalDelete: { ...prev.modalDelete, open: true },
                          })),
                        archive: () =>
                          this.onBulkArchive({
                            preventDefault: () => {},
                            stopPropagation: () => {},
                          } as any),
                        moveTo: () =>
                          this.setState(prev => ({
                            modalCopyMove: { ...prev.modalCopyMove, title: 'Move To', open: true },
                          })),
                        copyTo: () =>
                          this.setState(prev => ({
                            modalCopyMove: { ...prev.modalCopyMove, title: 'Copy To', open: true },
                          })),
                        convertTo: () =>
                          this.setState(prev => ({
                            modalConvertTo: {
                              ...prev.modalConvertTo,
                              title: 'Convert To',
                              open: true,
                            },
                          })),
                        createPhase: e => this.onCreatePhase(e),
                        editMultipleDetails: e => this.onEditMultipleDetails(e),
                        changeProject: e => this.onChangeProject(e),
                        submitTime: () => this.openModalSubmitTime(),
                        editHistory: () => this.openModalEditHistory(),
                      },
                      closeMenu: this.closeMenu,
                      onMarkAsRedFlag: this.onMarkAsRedFlag,
                      onMarkAsImportant: this.onMarkAsImportant,
                      followState: isUserFollowingConversation,
                      onUpdateConversation: this.props.onUpdateConversation
                        ? (id: number, updates: Partial<ConversationDetail>) =>
                            this.props.onUpdateConversation!(id, updates as any, true, 'header')
                        : undefined,
                    };

                    const visibleActions = ACTION_MENU_ITEMS.filter(
                      action =>
                        (action.mode === 'multi' || action.mode === 'both') &&
                        (!action.visible || action.visible(ctx)),
                    );

                    return (
                      <>
                        <li className="corgee_com_card-action-menu-header" role="presentation">
                          <i className="material-icons">check_box</i>
                          <span>{convs.length} cards selected</span>
                        </li>
                        <li role="presentation" className="corgee_com_card-action-menu-divider" />
                        {visibleActions.map(action =>
                        {
                          const label =
                            typeof action.label === 'function' ? action.label(ctx) : action.label;
                          const iconContent = ICON_CONTENT[action.key] ?? '';
                          return (
                            <li
                              key={action.key}
                              role="menuitem"
                              className="corgee_com_card-action-menu-item"
                            >
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={e => action.onClick(ctx, e)}
                              >
                                <i className={action.icon}>{iconContent}</i>
                                <span>{label}</span>
                              </button>
                            </li>
                          );
                        })}
                      </>
                    );
                  })()}
                {/* ─── SINGLE-SELECT MENU ─── */}
                {!isMultiple && (
                  <>
                    {/* Loading person enabled status */}
                    {isPersonCard && this.state.personIsEnabled === null && (
                      <li role="menuitem" className="corgee_com_card-action-menu-item">
                        <button
                          className="corgee_com_card-action-menu-item-button"
                          type="button"
                          disabled
                        >
                          <i className="material-icons">watch_later</i>
                          <span className="Loading-icons">Loading...</span>
                        </button>
                      </li>
                    )}
                    {/* Disabled person card: only show Restore option */}
                    {isPersonCard && this.state.personIsEnabled !== null && !effectiveIsEnable && (
                      <li role="menuitem" className="corgee_com_card-action-menu-item">
                        <button
                          className="corgee_com_card-action-menu-item-button"
                          type="button"
                          onClick={() =>
                          {
                            this.setState({ modalToggleUserStatus: true, modalUserIsEnabled: !!effectiveIsEnable });
                            this.closeMenu();
                          }}
                        >
                          <i className="material-icons">person_add</i>
                          <span>Restore User</span>
                        </button>
                      </li>
                    )}
                    {/* Hide all other options when person card is disabled or still loading */}
                    {
                      !(
                        isPersonCard &&
                        (this.state.personIsEnabled === null || !effectiveIsEnable)
                      ) && (
                        <>
                          {shouldShowProjectName &&
                            conversation.ProjectIsEnable == true &&
                            (CorgeeHelper.inArray(menusConv, 'goto-project') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button "
                                type="button"
                                onClick={this.onGoToProjectView}
                              >
                                <i className="fa fa-folder-open-o"></i>
                                <span>
                                    Go To{' '}
                                  <span className="corgee_com_card-action-menu-item-project-name">
                                    {conversation.ProjectName
                                      ? conversation.ProjectName
                                      : conversation.Title}
                                  </span>
                                </span>
                              </button>
                            </li>
                          )}
                          {(isInfowindowViewCard || !isConversationDetailsView) &&
                            (CorgeeHelper.inArray(menusConv, 'close-infowindow') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onOpenInfoWindow}
                              >
                                <i className="material-icons">chrome_reader_mode</i>
                                <span>
                                  {infoWindowIsOpen || isInfowindowViewCard
                                    ? 'Close Info Window'
                                    : 'Open Info Window'}
                                </span>
                              </button>
                            </li>
                          )}
                          {(isInfowindowViewCard || !isConversationDetailsView) &&
                            (CorgeeHelper.inArray(menusConv, 'opendetails-newtab') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onOpenDetailsNewTab}
                              >
                                <i className="material-icons">open_in_new</i>
                                <span>Open in a New Window</span>
                              </button>
                            </li>
                          )}
                          {(CorgeeHelper.inArray(menusConv, 'mark-redflag') ||
                            menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onMarkAsRedFlag}
                              >
                                <i className="corgee-icon-red-flag material-icons"></i>
                                <span>
                                  {valueIsRedFlag ? 'Unmark as Redflag' : 'Mark as Redflag'}
                                </span>
                              </button>
                            </li>
                          )}
                          {(CorgeeHelper.inArray(menusConv, 'mark-important') ||
                            menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onMarkAsImportant}
                              >
                                <i className="material-icons">priority_high</i>
                                <span>
                                  {valueIsImportant ? 'Unmark as Important' : 'Mark as Important'}
                                </span>
                              </button>
                            </li>
                          )}
                          {isLoadingMenu == false &&
                            (CorgeeHelper.inArray(menusConv, 'follow-conv') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onFollowConversation}
                              >
                                <i className="icons-menu-local-offer material-icons"></i>
                                <span>
                                  {isUserFollowingConversation === 1
                                    ? 'Unfollow conversation'
                                    : 'Follow conversation'}
                                </span>
                              </button>
                            </li>
                          )}
                          {isLoadingMenu == true &&
                            (CorgeeHelper.inArray(menusConv, 'follow-conv') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                              >
                                <i className="material-icons">watch_later</i>
                                <span className="Loading-icons">{'Loading...'}</span>
                              </button>
                            </li>
                          )}
                          {isProjectCard &&
                            (isInfowindowViewCard || !isConversationDetailsView) &&
                            (CorgeeHelper.inArray(menusConv, 'duplicate-project') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onDuplicateProject}
                              >
                                <i className="helper-icon-duplicate-project"></i>
                                <span>Duplicate Project</span>
                              </button>
                            </li>
                          )}
                          {HasItemCreationPermission &&
                            (CorgeeHelper.inArray(menusConv, 'create-itemtype') ||
                              menusConv.length === 0) && (
                            <li
                              role="menuitem"
                              className="corgee_com_card-action-menu-item dropdown-submenu"
                            >
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.openSubMenu}
                              >
                                <i className="material-icons">add_circle</i>
                                <span>Create</span>
                              </button>
                              {showSubMenu && (
                                <RenderInBody>
                                  <div>
                                    <ul
                                      className="corgee_com_card-action-submenu dropdown-menu"
                                      ref={this.corgeeSubMenuItemRef}
                                      tabIndex={0}
                                    >
                                      {subMenuItems.map(item => (
                                        <li
                                          key={'create-config-value-id-' + item.ConfigValueID}
                                          role="menuitem"
                                          className="corgee_com_card-action-menu-item create-item-menu"
                                          data-config-value-id={item.ConfigValueID}
                                          data-conversation-id={conversation.ConversationId}
                                          data-reply-id="0"
                                          data-config-value-text={item.Text}
                                        >
                                          <button
                                            className="corgee_com_card-action-menu-item-button"
                                            type="button"
                                            onClick={() => this.onCreateNewItem(item)}
                                          >
                                            <i className={'material-icons ' + item.Icon}></i>
                                            <span>{item.Text}</span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </RenderInBody>
                              )}
                            </li>
                          )}
                          {!isProjectCard &&
                            canEdit === true &&
                            (CorgeeHelper.inArray(menusConv, 'convert-to') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onConvertTo}
                              >
                                <i className="material-icons">repeat</i>
                                <span>Convert To</span>
                              </button>
                            </li>
                          )}
                          {!groupIsDefault &&
                            (CorgeeHelper.inArray(menusConv, 'remove-from-column') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onRemoveFromColumn}
                              >
                                <i className="material-icons">remove_circle_outline</i>
                                <span>Remove From Column</span>
                              </button>
                            </li>
                          )}
                          {(CorgeeHelper.inArray(menusConv, 'copylink-to-clipboard') ||
                            menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onCopyToClipboard}
                              >
                                <i className="material-icons">link</i>
                                <span>Copy link to Clipboard</span>
                              </button>
                            </li>
                          )}
                          {showMoveto &&
                            (CorgeeHelper.inArray(menusConv, 'move-to') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item ">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onMoveTo}
                              >
                                <i className="fa fa-share-square-o"></i>
                                <span>Move To</span>
                              </button>
                            </li>
                          )}
                          {(CorgeeHelper.inArray(menusConv, 'copy-to') ||
                            menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item ">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onCopyTo}
                              >
                                <i className="fa fa-clone"></i>
                                <span>Copy To</span>
                              </button>
                            </li>
                          )}
                          {!isProjectCard &&
                            !isPersonCard &&
                            (CorgeeHelper.inArray(menusConv, 'submit-time') ||
                              menusConv.length === 0) && (
                            <li
                              role="menuitem"
                              className="corgee_com_card-action-menu-item action-menu-submit-time-item"
                            >
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.openModalSubmitTime}
                              >
                                <i className="material-icons">schedule</i>
                                <span>Submit Time</span>
                              </button>
                            </li>
                          )}
                          {editDetail === true &&
                            !isProjectCard &&
                            !isPersonCard &&
                            (CorgeeHelper.inArray(menusConv, 'edit-history') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item ">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.openModalEditHistory}
                              >
                                <i className="material-icons">history</i>
                                <span>Edit Detail History</span>
                              </button>
                            </li>
                          )}
                          {isPersonCard &&
                            (CorgeeHelper.inArray(menusConv, 'edit-security-groups') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={() =>
                                {
                                  this.setState({ modalEditSecurityGroups: true });
                                  this.closeMenu();
                                }}
                              >
                                <i className="material-icons">supervisor_account</i>
                                <span>Edit Security Group Permissions</span>
                              </button>
                            </li>
                          )}
                          {isPersonCard &&
                            (CorgeeHelper.inArray(menusConv, 'toggle-user-status') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={() =>
                                {
                                  this.setState({ modalToggleUserStatus: true, modalUserIsEnabled: !!effectiveIsEnable });
                                  this.closeMenu();
                                }}
                              >
                                <i className="material-icons">{'account_circle'}</i>
                                <span>{effectiveIsEnable ? 'Disable User' : 'Restore User'}</span>
                              </button>
                            </li>
                          )}
                          {canEdit === true &&
                            (CorgeeHelper.inArray(menusConv, 'edit') || menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onEdit}
                              >
                                <i className="icon-pencil-icon"></i>
                                <span>{'Edit'}</span>
                              </button>
                            </li>
                          )}
                          {canDelete === true &&
                            (CorgeeHelper.inArray(menusConv, 'delete') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onDelete}
                              >
                                <i className="icon-trash-icon"></i>
                                <span>{'Delete'}</span>
                              </button>
                            </li>
                          )}
                          {!isPersonCard &&
                            !isProjectCard &&
                            HasItemCreationPermission &&
                            (CorgeeHelper.inArray(menusConv, 'duplicate-item') ||
                              menusConv.length === 0) && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onDuplicateItem}
                              >
                                <i className="material-icons">control_point_duplicate</i>
                                <span>Duplicate Item</span>
                              </button>
                            </li>
                          )}
                          {canEdit === true &&
                            (CorgeeHelper.inArray(menusConv, 'archive') ||
                              menusConv.length === 0) &&
                            this.props.conversation.ConfigValueId !== 15 && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item ">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onArchive}
                              >
                                <i className="icon2-filedown material-icons"></i>
                                <span>{conversation.IsArchived ? 'Unarchive' : 'Archive'}</span>
                              </button>
                            </li>
                          )}
                          {CorgeeHelper.inArray(menusConv, 'restore') && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onRestore}
                              >
                                <i className="material-icons">restore_from_trash</i>
                                <span>Restore</span>
                              </button>
                            </li>
                          )}
                          {CorgeeHelper.inArray(menusConv, 'delete-permanently') && (
                            <li role="menuitem" className="corgee_com_card-action-menu-item">
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={this.onDeletePermanently}
                              >
                                <i className="icon-trash-icon"></i>
                                <span>Delete Permanently</span>
                              </button>
                            </li>
                          )}
                        </>
                      ) /* end !(isPersonCard && !IsEnable) */
                    }
                  </>
                )}
                {/* ─────────────────────────────────────────── */}
              </ul>
            </RenderInBody>
          )}
        </div>

        {isOpen && (
          <RenderInBody>
            <div className="corgee_com_card-action-menu-backdrop" onClick={this.closeMenu}></div>
          </RenderInBody>
        )}

        {modal.open && (
          <SubmitTime
            openmodal={this.state.modal.open}
            title={this.state.modal.title}
            onclose={this.onCloseModal}
            conversation={conversation as any}
            api={api}
            typesubmittime={1}
            isedit={false}
            onSubmitTime={this.props.onSubmitTime}
            socketconnectionid={this.props.SocketConnectionId}
            corgeefunctions={this.props.corgeeFunctions}
            onaddreply={this.props.onAddReply}
            phaseselected={this.props.phaseSelected}
          />
        )}

        {modalEditHistory.open && (
          <EditDetailsHistory
            customfield={{ ConversationId: this.props.conversation.ConversationId } as any}
            modal={this.state.modalEditHistory}
            onclosemodal={this.onCloseModalEditHistory}
            className={''}
            openModal={this.state.modalEditHistory.open}
            title={this.state.modalEditHistory.title}
            onClose={this.onCloseModalEditHistory}
            isDraggable={true}
            closeOnEscape={true}
            width={360}
            height={120}
            hasMaxHeight={true}
            allowSaveAndNewCheckBox={false}
            hideActionButtons={true}
            conversation={conversation as any}
            api={api}
            onSubmitTime={this.props.onSubmitTime as any}
            {...(this.props as any)}
          />
        )}

        {linkCopied && (
          <RenderInBody>
            <div className="corgee_com_notification-bubble-container clipboard-notification-r">
              <div className="corgee_com_notificaiton-bubble">
                Link has been copied to clipboard
              </div>
            </div>
          </RenderInBody>
        )}
        {conversationDeleted && (
          <RenderInBody>
            <div className="corgee_com_notification-bubble-container deleted-notification-r">
              <div className="corgee_com_notificaiton-bubble">The item has been deleted</div>
            </div>
          </RenderInBody>
        )}
        {conversationArchive && (
          <RenderInBody>
            <div className="corgee_com_notification-bubble-container archive-notification-r">
              <div className="corgee_com_notificaiton-bubble">Item archive</div>
            </div>
          </RenderInBody>
        )}
        {modalConvertTo.open && (
          <RenderBody>
            <ConvertToModal
              open={modalConvertTo.open}
              setOpen={() =>
                this.setState(prev => ({ modalConvertTo: { ...prev.modalConvertTo, open: false } }))
              }
              conversations={
                (selectedConversations.length > 0 ? selectedConversations : [conversation]) as any
              }
              existsInvalidItems={existsInvalidItems}
              api={api as any}
              onConverToProccess={this.onConverToProccess}
            />
          </RenderBody>
        )}

        {modalDelete.open && createPortal(
          <DeleteConversationModel
            open={modalDelete.open}
            setOpen={() =>
              this.setState(prev => ({ modalDelete: { ...prev.modalDelete, open: false } }))
            }
            conversations={
              selectedConversations.length > 1 ? selectedConversations : [conversation]
            }
            SocketConnectionId={this.props.SocketConnectionId || ''}
            deleteConversationFunction={() =>
            {
              const convs =
                selectedConversations.length > 1
                  ? selectedConversations
                  : [this.props.conversation];

              if (convs.length > 1 && this.props.corgeeFunctions?.bulkDeleteConversations)
              {
                // Single atomic state update — avoids batching / stale-closure issues
                this.props.corgeeFunctions.bulkDeleteConversations(
                  convs.map(c => c.ConversationId),
                );
              }
              else
              {
                convs.forEach(
                  c =>
                    this.props.corgeeFunctions?.deleteConversation &&
                    this.props.corgeeFunctions.deleteConversation(null as any, c as Conversation),
                );
              }
            }}
            onDeletedConversation={this.onDeletedConversation}
          />,
          document.body,
        )}

        {modalCopyMove.open && createPortal(
          <MoveCopyToModal
            open={modalCopyMove.open}
            title={modalCopyMove.title}
            setOpen={() =>
              this.setState(prev => ({ modalCopyMove: { ...prev.modalCopyMove, open: false } }))
            }
            conversations={
              selectedConversations.length > 1
                ? selectedConversations
                : [conversation]
            }
            SocketConnectionId={this.props.SocketConnectionId || ''}
            isInfowindowViewCard={isInfowindowViewCard ?? false}
            isMultiple={selectedConversations.length > 1}
            moveFromColumn={this.props.corgeeFunctions?.onMoveFromColumn as any}
            removeFromColumn={this.props.corgeeFunctions?.onRemoveFromColumn as any}
            addToColumn={this.props.corgeeFunctions?.addToColumn}
            bulkRemoveFromColumnUi={this.props.corgeeFunctions?.bulkDeleteConversations}
          />,
          document.body,
        )}

        {/* createPortal (not RenderBody) keeps the modal inside the app tree so ToastProvider is reachable */}
        {modalCreatePhase.open && createPortal(
          <CreatePhase
            config={{ apiUrl: this.props.api.url, token: this.props.api.token }}
            parameters={
              {
                ConversationIds: (modalCreatePhase.config ?? selectedConversations) as any,
                Multiple: true,
                SocketConnectionId: this.props.SocketConnectionId || '',
              } as any
            }
            modal={{ open: modalCreatePhase.open, title: 'Add Phase' }}
            onclosemodal={() =>
              this.setState(prev => ({
                modalCreatePhase: { ...prev.modalCreatePhase, open: false },
              }))
            }
            onSuccess={conversationIds =>
            {
              conversationIds.forEach(convId =>
              {
                window.dispatchEvent(
                  new CustomEvent('corgee:conversation-phases-updated', {
                    detail: { conversationId: convId },
                  }),
                );
              });
            }}
            loadingbarstart={() => {}}
            loadingbarcomplete={() => {}}
          />,
          document.body,
        )}
        {modalEditMultipleCard.open &&
          createPortal(
            <EditMultipleCards
              config={modalEditMultipleCard.config}
              isOpen={modalEditMultipleCard.open}
              onIsOpen={() =>
                this.setState(prev => ({
                  modalEditMultipleCard: { ...prev.modalEditMultipleCard, open: false },
                }))
              }
            />,
            document.body,
          )}

        {this.state.modalChangeProject.open && createPortal(
          <ChangeProjectModal
            open={this.state.modalChangeProject.open}
            onClose={() =>
              this.setState(prev => ({
                modalChangeProject: { ...prev.modalChangeProject, open: false },
              }))
            }
            conversations={
              selectedConversations.length > 0 ? selectedConversations : [conversation]
            }
            onProjectChanged={results =>
            {
              // Full results, not just ids: the consumer needs ProjectId/ProjectName to show
              // the new project on the card instead of dropping it from the dashboard.
              this.props.corgeeFunctions?.onConversationsProjectChanged?.(results);
              this.setState({ selectedConversations: [] });
            }}
          />,
          document.body,
        )}

        <EditSecurityGroupsModal
          open={this.state.modalEditSecurityGroups}
          onClose={() => this.setState({ modalEditSecurityGroups: false })}
          personId={conversation.PersonId ?? 0}
          personName={conversation.Title}
        />

        <ToggleUserStatusModal
          open={this.state.modalToggleUserStatus}
          onClose={() => this.setState({ modalToggleUserStatus: false })}
          personId={conversation.PersonId ?? 0}
          personName={conversation.Title}
          isEnabled={this.state.modalUserIsEnabled}
          onSuccess={(_personId, newState) =>
          {
            this.props.onUpdateConversation?.(
              conversation.ConversationId,
              { IsEnable: newState } as any,
              true,
              'header',
            );
          }}
        />
      </div>
    );
  }
}

function ActionMenuConversationWrapper(props: Omit<ActionMenuConversationProps, 'navigate'>)
{
  const navigate = useNavigate();
  return <ActionMenuConversation {...props} navigate={navigate} />;
}

export default ActionMenuConversationWrapper;
